<script setup lang="ts">
import {
	IconButton,
	Dialog,
	DialogContent,
	DialogTrigger,
} from '@laam/ui/base';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';

import LazyImage from '~/components/LazyImage.vue';

interface ImageViewerProps {
	media: {
		src: string;
		alt: string;
		height: number;
		width: number;
	}[];

	index: number;
}

const props = defineProps<ImageViewerProps>();

const imgIndex = ref(0);

const imageViewerGalleryImageRef = ref();

const imageViewerMobileRef = ref();

watch(
	() => props.index,
	(val) => {
		imgIndex.value = val;
	},
);

function scrollIntoView(id: number) {
	const element = imageViewerGalleryImageRef.value[id];
	if (element) {
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'center',
		});
	}

	const element2 = imageViewerMobileRef.value[id];
	if (element2) {
		element2.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'center',
		});
	}
}

function onClickThumb(index: number) {
	imgIndex.value = index;
	scrollIntoView(index);
}

const nextPrevImg = (direction: string) => {
	if (direction === 'right') {
		if (imgIndex.value + 1 >= props.media.slice(0, 15).length) {
			imgIndex.value = 0;
		} else {
			imgIndex.value += 1;
		}
		scrollIntoView(imgIndex.value);
	} else if (direction === 'left') {
		if (imgIndex.value - 1 < 0) {
			imgIndex.value = props.media.slice(0, 15).length - 1;
		} else {
			imgIndex.value -= 1;
		}
		scrollIntoView(imgIndex.value);
	}
};
</script>

<template>
	<Dialog>
		<DialogTrigger class="absolute top-0 bottom-0 left-0 right-0">
			<slot></slot>
		</DialogTrigger>
		<DialogContent
			:show-icon-with-bg="true"
			class="flex flex-col w-full h-full gap-0 border-none rounded-none p-none"
		>
			<div class="relative max-w-full m-auto overflow-hidden">
				<div class="relative slider">
					<div class="flex slides gap-xl">
						<div
							v-for="(content, loopIndex) in props.media"
							:key="loopIndex"
							ref="imageViewerMobileRef"
							class="relative snap-always overflow-hidden image-box min-h-[400px]"
						>
							<LazyImage
								class="pdp__image-slider-image image"
								:src="props.media[imgIndex]!.src"
								:alt="content.alt"
								:width="450"
							/>
						</div>
					</div>

					<IconButton
						class="absolute bottom-1/2 left-3xl hover:cursor-pointer"
						variant="secondary"
						size="xl"
						rounded="full"
						@click.stop="nextPrevImg('left')"
					>
						<PhCaretLeft size="20" />
					</IconButton>
					<IconButton
						class="absolute bottom-1/2 right-3xl hover:cursor-pointer"
						variant="secondary"
						size="xl"
						rounded="full"
						@click.stop="nextPrevImg('right')"
					>
						<PhCaretRight size="20" />
					</IconButton>

					<div
						class="absolute left-0 right-0 inline-flex items-center mx-auto bg-white p-md gap-md rounded-xl bg-opacity-90 backdrop-blur-sm bottom-3xl w-fit"
					>
						<div
							v-for="(_, paginationIndex) in props.media.slice(0, 15)"
							:key="paginationIndex"
							class="rounded-full w-md h-md"
							:class="{
								'bg-gray-300': imgIndex !== paginationIndex,
								'bg-gray-800': imgIndex === paginationIndex,
							}"
							@click="
								() => {
									imgIndex = paginationIndex;
									scrollIntoView(imgIndex);
								}
							"
						/>
					</div>
				</div>
			</div>

			<div ref="gallery" class="overflow-scroll p-7xl">
				<div class="flex flex-row gap-xs">
					<div
						v-for="(content, i) in media"
						ref="imageViewerGalleryImageRef"
						:key="i"
						class="image-box !min-w-max !min-h-none bg-white cursor-pointer"
						:class="i === imgIndex ? 'opacity-100' : 'opacity-60'"
						@click.stop="onClickThumb(i)"
					>
						<LazyImage
							class="pdp__image-slider-image rounded-xs"
							:src="content.src"
							:alt="content.alt"
							width="90"
						/>
					</div>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<style scoped>
.slider {
	overflow: hidden;
}

.slides {
	display: flex;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scroll-behavior: smooth;
	-webkit-overflow-scrolling: touch;
}
.slides > div {
	scroll-snap-align: start;
	flex-shrink: 0;
	position: relative;
}
</style>
